import { useTranslation } from 'react-i18next';
import { useTopUpCoversUsage } from 'features/credits/hooks/use-credits';
import { WindowUsage } from 'features/usage/services/usage-service';
import { formatPercent, formatWindowName } from 'features/usage/utils/usage-format';
import { UsageBar } from './usage-bar';
import { UsageCreditCard } from './usage-credit-card';
import { resetTime, UsageWindowLimits } from './usage-window-limit';

/** The daily limit window (or the first window when a plan has no daily one), plus any others. */
export function UsageTodayCard({ windows }: { windows: WindowUsage[] }) {
  const { t } = useTranslation();
  const topUpCovers = useTopUpCoversUsage();

  const dailyWindow = windows.find((window) => window.type === 'daily') ?? windows[0];
  if (!dailyWindow) return null;

  const isDaily = dailyWindow.type === 'daily';
  const otherWindows = windows.filter((window) => window !== dailyWindow);

  // Same clock-time wording as dev's limit rows: "Resets tomorrow 00:00".
  const dailyResetsIn = dailyWindow.resets_at
    ? resetTime(dailyWindow.resets_at, t('TOMORROW', { defaultValue: 'tomorrow' }))
    : '';

  const getFooter = () => {
    if (dailyResetsIn) {
      return t('WINDOW_RESETS', { when: dailyResetsIn, defaultValue: 'Resets {{when}}' });
    }
    if (isDaily) return t('USAGE_RESETS_DAILY', { defaultValue: 'Resets every day' });
    return t('USAGE_RESETS_HOURLY', { defaultValue: 'Resets every hour' });
  };

  return (
    <UsageCreditCard
      title={
        isDaily
          ? t('DAILY_LIMIT', { defaultValue: 'Daily limit' })
          : formatWindowName(dailyWindow.name, t)
      }
      value={formatPercent(Math.max(0, 100 - dailyWindow.percent), 2)}
      unit={t('USAGE_LEFT', { defaultValue: 'left' })}
      footer={getFooter()}
    >
      <UsageBar percent={dailyWindow.percent} calm={topUpCovers} />
      <p className="text-xs text-muted-foreground">
        {t('USAGE_PERCENT_USED', {
          percent: formatPercent(dailyWindow.percent, 2),
          defaultValue: '{{percent}} used',
        })}
      </p>
      <UsageWindowLimits windows={otherWindows} calm={topUpCovers} />
    </UsageCreditCard>
  );
}
